import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LayoutDashboard, LogOut, User, Search } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [visitors, setVisitors] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.post("/visitors/increment").catch(() => {});
    api.get("/visitors/count").then((r) => setVisitors(r.data.count)).catch(() => {});
  }, []);

  const dashboardHref = () => {
    if (!user) return "/login";
    if (["super_admin", "major_admin"].includes(user.role)) return "/admin";
    if (user.role === "venue_owner" || user.role === "staff") return "/owner";
    return "/account";
  };

  return (
    <header className="glass-nav sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <div className="h-9 w-9 rounded-full bg-[color:var(--wine)] flex items-center justify-center">
            <span className="font-display text-[color:var(--gold-2)] text-xl leading-none">S</span>
          </div>
          <div className="leading-tight">
            <div className="font-display text-xl text-[color:var(--wine)]">Shubh Muhurat</div>
            <div className="text-[10px] tracking-widest-2 uppercase text-[color:var(--ink-2)]">Weddings · Venues · Catering</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-sm">
          <NavLink to="/" className={({isActive}) => isActive ? "text-[color:var(--wine)] font-medium" : "text-neutral-700 hover:text-[color:var(--wine)]"} data-testid="nav-home">Home</NavLink>
          <NavLink to="/properties" className={({isActive}) => isActive ? "text-[color:var(--wine)] font-medium" : "text-neutral-700 hover:text-[color:var(--wine)]"} data-testid="nav-properties">Properties</NavLink>
          <NavLink to="/highlighted" className={({isActive}) => isActive ? "text-[color:var(--wine)] font-medium" : "text-neutral-700 hover:text-[color:var(--wine)]"} data-testid="nav-highlighted">Highlighted</NavLink>
          <NavLink to="/blogs" className={({isActive}) => isActive ? "text-[color:var(--wine)] font-medium" : "text-neutral-700 hover:text-[color:var(--wine)]"} data-testid="nav-blogs">Blogs</NavLink>
          <NavLink to="/contact" className={({isActive}) => isActive ? "text-[color:var(--wine)] font-medium" : "text-neutral-700 hover:text-[color:var(--wine)]"} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <VisitorPill count={visitors} />
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to={dashboardHref()} className="btn-ghost !py-2 !px-4 text-sm" data-testid="dashboard-link">
                <LayoutDashboard className="w-4 h-4 inline mr-1" /> Panel
              </Link>
              <button onClick={async () => { await logout(); nav("/"); }} className="btn-wine !py-2 !px-4 text-sm" data-testid="logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/login" className="btn-ghost !py-2 !px-4 text-sm" data-testid="login-link">Sign in</Link>
              <Link to="/register" className="btn-wine !py-2 !px-4 text-sm" data-testid="register-link">List your venue</Link>
            </div>
          )}
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{height:0}} animate={{height:"auto"}} exit={{height:0}} className="lg:hidden overflow-hidden border-t border-[color:var(--line)] bg-white/95">
            <div className="px-6 py-4 flex flex-col gap-3 text-sm">
              {["/", "/properties", "/highlighted", "/blogs", "/contact"].map((p, i) => (
                <Link key={p} to={p} onClick={()=>setOpen(false)} className="text-neutral-800">{["Home","Properties","Highlighted","Blogs","Contact"][i]}</Link>
              ))}
              {user ? (
                <>
                  <Link to={dashboardHref()} onClick={()=>setOpen(false)} className="btn-ghost text-center">Panel</Link>
                  <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="btn-wine">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={()=>setOpen(false)} className="btn-ghost text-center">Sign in</Link>
                  <Link to="/register" onClick={()=>setOpen(false)} className="btn-wine text-center">List your venue</Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function VisitorPill({ count }) {
  return (
    <div className="hidden md:flex flex-col items-end" data-testid="visitor-counter">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[color:var(--line)] shadow-sm">
        <span className="pulse-dot"/>
        <span className="text-xs font-medium text-neutral-800">{count == null ? "…" : Number(count).toLocaleString("en-IN")}</span>
        <span className="text-[10px] uppercase tracking-widest-2 text-neutral-500">views*</span>
      </div>
      <span className="text-[9px] text-neutral-400 mt-1 max-w-[220px] text-right leading-tight">*Every page refresh counts as one visit</span>
    </div>
  );
}
